/**
 * richTextModel — helpers PURS sur le format JSON Tiptap (ProseMirror).
 *
 * Extraits de RichTextEditor.jsx (audit perf 2026-07-13) : ces fonctions ne
 * touchent que du JSON/string, AUCUN import @tiptap. Les garder ici permet à
 * Inspector et TextLayer (rendu canvas) de les utiliser sans tirer TipTap
 * (~860 kB source) dans le bundle initial. L'éditeur TipTap lui-même est
 * chargé en lazy au premier double-clic sur un calque texte.
 *
 * Déplacé de `components/` vers `utils/` le 2026-08-21 : le moteur de modèles
 * réglementaires (utils/regulatoryTemplate.js) doit lire le style RÉEL d'un
 * calque, et aucun module de `utils/` n'importe de `components/`.
 *
 * ── LE POINT À COMPRENDRE AVANT DE TOUCHER À CE FICHIER ──────────────────────
 * Un calque texte porte sa mise en forme à DEUX endroits, et ils ne disent pas
 * toujours la même chose :
 *   - à plat sur l'élément (`el.color`, `el.font`, `el.size`) — ce qu'écrit
 *     l'inspecteur ;
 *   - dans les marks `textStyle` de `el.content` — ce qu'écrit la barre
 *     flottante d'édition (double-clic sur le calque).
 * **Au rendu, les marks GAGNENT** (cf. `richTextToHTML` plus bas, et
 * `canvas/TextLayer.jsx`). Toute lecture du style d'un calque doit donc passer
 * par `styleEffectifTexte()` et jamais par `el.color` seul : c'est l'oubli qui
 * a fait revenir en noir un code UFI passé en rouge, à l'application d'un
 * modèle .lfgr (retour utilisateur du 2026-08-21).
 *
 * ── ET LE CORPS N'EST PAS DANS LA MÊME UNITÉ DES DEUX CÔTÉS ─────────────────
 * `el.size` est en POINTS. La mark `fontSize`, elle, est une longueur CSS posée
 * dans le conteneur de `canvas/TextLayer.jsx`, qui est grossi ×TEXT_SCALE puis
 * réduit d'autant (technique anti sub-pixel) et dont l'unité de base est le
 * millimètre du canvas. Un même nombre n'y veut donc PAS dire la même chose :
 * 12 pt s'y écrivent 12 × 0,3528 × 10 ≈ 42,3 px. Confondre les deux donne un
 * corps trois fois et demie trop grand, ou trois fois et demie trop petit.
 * Toute traversée passe par `ptVersPxMark` / `pxMarkVersPt`, jamais à la main.
 */

/** 1 pt = 0,3528 mm (cf. canvas/helpers.js). */
const PT_VERS_MM = 0.3528;
/** Grossissement du conteneur de texte (cf. canvas/TextLayer.jsx). */
const ECHELLE_TEXTE = 10;
/** Facteur unique entre le corps en points et la longueur CSS d'une mark. */
const PT_VERS_PX_MARK = PT_VERS_MM * ECHELLE_TEXTE;

/** Corps en points → longueur CSS de la mark `fontSize`, arrondie au centième. */
export function ptVersPxMark(pt) {
    const n = parseFloat(pt);
    if (!Number.isFinite(n)) return null;
    return Math.round(n * PT_VERS_PX_MARK * 100) / 100;
}

/** Longueur CSS d'une mark `fontSize` → corps en points, arrondi au dixième. */
export function pxMarkVersPt(px) {
    const n = parseFloat(px);
    if (!Number.isFinite(n)) return null;
    return Math.round((n / PT_VERS_PX_MARK) * 10) / 10;
}

/**
 * Analyse un document Tiptap pour détecter les valeurs uniformes ou mixtes
 * sur tous les runs de texte. Permet à l'inspecteur de griser les contrôles
 * dont la valeur n'est pas unique (style Photoshop/Illustrator).
 *
 * Retourne un objet :
 *   { fontFamily: 'unique'|'mixed'|null, value: string|null }
 *   { fontSize: ..., value: number|null }
 *   { color: ..., value: string|null }
 *   { bold: 'all'|'none'|'mixed', italic: ..., underline: ... }
 */
export function analyzeContent(json) {
    const result = {
        fontFamily: { state: null, value: null },
        fontSize: { state: null, value: null },
        color: { state: null, value: null },
        bold: 'none',
        italic: 'none',
        underline: 'none',
    };
    if (!json || !json.content) return result;

    let runCount = 0;
    let boldCount = 0, italicCount = 0, underlineCount = 0;
    const fontFamilies = new Set();
    const fontSizes = new Set();
    const colors = new Set();

    for (const para of json.content) {
        if (para.type !== 'paragraph') continue;
        for (const run of para.content || []) {
            if (run.type !== 'text' || !run.text) continue;
            runCount++;
            const marks = run.marks || [];
            const ts = marks.find(m => m.type === 'textStyle')?.attrs || {};
            fontFamilies.add(ts.fontFamily || '__default__');
            fontSizes.add(ts.fontSize || '__default__');
            colors.add(ts.color || '__default__');
            if (marks.some(m => m.type === 'bold')) boldCount++;
            if (marks.some(m => m.type === 'italic')) italicCount++;
            if (marks.some(m => m.type === 'underline')) underlineCount++;
        }
    }

    if (runCount === 0) return result;

    const setToState = (set) => {
        if (set.size === 1) {
            const v = [...set][0];
            return { state: 'unique', value: v === '__default__' ? null : v };
        }
        return { state: 'mixed', value: null };
    };
    result.fontFamily = setToState(fontFamilies);
    result.fontSize = setToState(fontSizes);
    if (result.fontSize.value && typeof result.fontSize.value === 'string') {
        result.fontSize.value = parseFloat(result.fontSize.value);
    }
    result.color = setToState(colors);
    result.bold = boldCount === 0 ? 'none' : boldCount === runCount ? 'all' : 'mixed';
    result.italic = italicCount === 0 ? 'none' : italicCount === runCount ? 'all' : 'mixed';
    result.underline = underlineCount === 0 ? 'none' : underlineCount === runCount ? 'all' : 'mixed';

    return result;
}

/**
 * Convertit un JSON Tiptap en HTML stylé pour le rendu dans le canvas.
 * Utilisé par LabelCanvas pour afficher le calque texte avec son rich text.
 */
export function richTextToHTML(json) {
    if (!json || !json.content) return '';
    return json.content.map(renderNode).join('');
}

// Une valeur recopiée dans `style="…"` vient du fichier, et le fichier peut être
// écrit par n'importe qui : la page d'aperçu partagée, publique, rend le
// projet d'un autre. Un `"` y fermait l'attribut et ouvrait la porte à un
// gestionnaire `onmouseover` ; un `;` y ajoutait des déclarations CSS. Une
// valeur légitime (`#E11D48`, `rgb(…)`, `center`) ne contient aucun de ces
// caractères : elle sort à l'identique.
function valeurCss(v) {
    return String(v).replace(/[";{}<>&]/g, '');
}

function renderNode(node) {
    if (node.type === 'paragraph') {
        const align = valeurCss(node.attrs?.textAlign || 'left');
        const inner = (node.content || []).map(renderNode).join('');
        // text-align: justify n'étire pas la dernière ligne d'un paragraphe.
        // text-align-last: justify force toutes les lignes (y compris seule
        // ligne courte) à occuper toute la largeur du conteneur.
        //
        // Drapeau ADDITIF lastLineJustify:false → on revient à la typographie
        // STANDARD (dernière ligne non étirée), utilisé par l'import PDF pour
        // reproduire un paragraphe justifié fidèle (façon Acrobat). Absent du
        // contenu existant ⇒ comportement inchangé (text-align-last: justify).
        const lastAlign = (align === 'justify' && node.attrs?.lastLineJustify !== false)
            ? 'justify' : 'auto';
        return `<p style="text-align:${align};text-align-last:${lastAlign};margin:0;">${inner || '<br/>'}</p>`;
    }
    if (node.type === 'text') {
        let style = '';
        let openTags = '';
        let closeTags = '';
        (node.marks || []).forEach(mark => {
            if (mark.type === 'bold') { openTags += '<strong>'; closeTags = '</strong>' + closeTags; }
            else if (mark.type === 'italic') { openTags += '<em>'; closeTags = '</em>' + closeTags; }
            else if (mark.type === 'underline') { openTags += '<u>'; closeTags = '</u>' + closeTags; }
            else if (mark.type === 'textStyle') {
                if (mark.attrs?.color) style += `color:${valeurCss(mark.attrs.color)};`;
                if (mark.attrs?.fontFamily) {
                    // Tiptap stocke souvent fontFamily="\"Arial\", sans-serif" (avec
                    // doubles quotes autour du nom — produites par notre helper
                    // fontNameToCSS). Comme on injecte style dans un attribut HTML
                    // wrappé par double-quotes (style="..."), ces guillemets
                    // internes cassent l'attribut et le navigateur ignore tout le
                    // font-family → la mark fontFamily est perdue et le span
                    // hérite du wrapper (typo globale du calque). On normalise
                    // les double-quotes en single-quotes, qui sont valides en
                    // CSS pour les noms de police avec espaces.
                    const ff = valeurCss(String(mark.attrs.fontFamily).replace(/"/g, "'"));
                    style += `font-family:${ff};`;
                }
                // Le corps circule sous DEUX formes selon son origine : un nombre nu
                // (12) quand notre barre d'édition l'a posé, et une chaîne avec unité
                // ("12px") quand Tiptap l'a relu depuis du HTML — c'est son parseHTML
                // qui rend `element.style.fontSize`, jamais dépouillé. Concaténer `px`
                // à l'aveugle donnait « font-size:12pxpx », que le navigateur jette :
                // le corps disparaissait du rendu et de l'export. On normalise donc au
                // nombre avant de reposer l'unité, une fois.
                if (mark.attrs?.fontSize) {
                    const corps = parseFloat(mark.attrs.fontSize);
                    if (Number.isFinite(corps)) style += `font-size:${corps}px;`;
                }
            }
        });
        const escaped = (node.text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        return style ? `${openTags}<span style="${style}">${escaped}</span>${closeTags}` : `${openTags}${escaped}${closeTags}`;
    }
    return '';
}

/**
 * Convertit une string brute (l'ancien format el.text) en JSON Tiptap minimal.
 * Préserve les sauts de ligne en plusieurs paragraphes.
 */
export function plainTextToTiptap(text, alignement) {
    // `alignement` doit porter celui du CALQUE. L'extension TextAlign de Tiptap
    // est configurée avec `defaultAlignment: 'left'` : un paragraphe créé sans
    // attribut se sérialise donc en `textAlign: 'left'`, et la synchronisation
    // de `canvas/TextLayer.jsx` recopie ce 'left' dans `el.textAlign` à la
    // PREMIÈRE frappe. Un texte centré sautait ainsi à gauche dès qu'on le
    // corrigeait — à l'écran, dans le projet enregistré, et dans tous les
    // modèles .lfgr faits ensuite, `textAlign` faisant partie de la mise en
    // forme capturée.
    const attrs = alignement ? { textAlign: alignement } : null;
    if (!text) return { type: 'doc', content: [{ type: 'paragraph', ...(attrs ? { attrs } : {}) }] };
    const lines = text.split('\n');
    return {
        type: 'doc',
        content: lines.map(line => ({
            type: 'paragraph',
            ...(attrs ? { attrs } : {}),
            content: line ? [{ type: 'text', text: line }] : [],
        })),
    };
}

/**
 * Extrait le texte brut depuis un JSON Tiptap (pour fallback / undo legacy).
 */
export function tiptapToPlainText(json) {
    if (!json || !json.content) return '';
    return json.content.map(p => (p.content || []).map(t => t.text || '').join('')).join('\n');
}

/* ────────────────────────────────────────────────────────────────────────────
   Style EFFECTIF, et écriture d'un style qui prend vraiment effet.
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Tiptap sérialise `fontFamily` sous la forme CSS produite par `fontNameToCSS`
 * — souvent `"Arial", sans-serif`, guillemets compris. Les propriétés à plat,
 * elles, portent le NOM SEUL (`el.font === 'Arial'`). On convertit dans le sens
 * mark → plat ; le sens inverse vit dans RichTextEditor (`fontNameToCSS`), qui
 * dépend de TipTap.
 */
export function nomDePoliceDepuisCSS(css) {
    if (!css || typeof css !== 'string') return null;
    const premier = css.split(',')[0].trim();
    const nu = premier.replace(/^["']|["']$/g, '').trim();
    return nu || null;
}

/**
 * Le style RÉELLEMENT PEINT d'un calque texte : les marks uniformes du rich
 * text l'emportent sur les propriétés à plat, exactement comme au rendu.
 *
 * Une valeur `mixed` (plusieurs couleurs dans le même calque) ne peut pas se
 * réduire à une propriété unique : on retombe alors sur le plat et on le SIGNALE
 * par `mixte`, pour que l'appelant puisse prévenir au lieu d'aplatir en silence.
 *
 * @param {{color?:string,font?:string,size?:number,content?:object}} el
 * @returns {{color:string|undefined, font:string|undefined, size:number|undefined,
 *            mixte:{color:boolean,font:boolean,size:boolean}}}
 */
export function styleEffectifTexte(el) {
    const plat = { color: el?.color, font: el?.font, size: el?.size };
    const mixte = { color: false, font: false, size: false };
    if (!el || !el.content || !el.content.content) return { ...plat, mixte };

    const a = analyzeContent(el.content);
    if (a.color.state === 'unique' && a.color.value) plat.color = a.color.value;
    else if (a.color.state === 'mixed') mixte.color = true;

    if (a.fontFamily.state === 'unique' && a.fontFamily.value) {
        const nom = nomDePoliceDepuisCSS(a.fontFamily.value);
        if (nom) plat.font = nom;
    } else if (a.fontFamily.state === 'mixed') mixte.font = true;

    // La mark est une longueur CSS du conteneur grossi : on la ramène en points,
    // l'unité de `el.size` et du champ Corps de l'inspecteur.
    if (a.fontSize.state === 'unique' && a.fontSize.value) {
        const pt = pxMarkVersPt(a.fontSize.value);
        if (pt) plat.size = pt;
    }
    else if (a.fontSize.state === 'mixed') mixte.size = true;

    return { ...plat, mixte };
}

/**
 * Valeur DOMINANTE de chaque attribut de style, pondérée par le nombre de
 * caractères qui la portent.
 *
 * Sert quand une valeur unique n'existe pas — un mot en rouge dans une phrase
 * noire. Le modèle ne peut pas reposer ce rouge-là sur un texte différent (les
 * mots ne sont plus les mêmes), mais reposer la couleur de la MAJORITÉ du texte
 * vaut infiniment mieux que reposer `el.color`, qui est resté au noir du
 * builder et que personne n'a jamais vu.
 */
export function styleDominantTexte(content) {
    const poids = { color: new Map(), fontFamily: new Map(), fontSize: new Map() };
    const parcourir = (noeud) => {
        if (!noeud || typeof noeud !== 'object') return;
        if (noeud.type === 'text' && noeud.text) {
            const ts = (noeud.marks || []).find((m) => m && m.type === 'textStyle')?.attrs || {};
            for (const cle of ['color', 'fontFamily', 'fontSize']) {
                const v = ts[cle];
                if (v === undefined || v === null || v === '') continue;
                poids[cle].set(v, (poids[cle].get(v) || 0) + noeud.text.length);
            }
        }
        for (const enfant of (noeud.content || [])) parcourir(enfant);
    };
    parcourir(content);
    const gagnant = (m) => {
        let best = null, max = 0;
        for (const [v, n] of m) if (n > max) { max = n; best = v; }
        return best;
    };
    const taille = gagnant(poids.fontSize);
    return {
        color: gagnant(poids.color),
        font: nomDePoliceDepuisCSS(gagnant(poids.fontFamily)),
        size: taille ? pxMarkVersPt(taille) : null,   // mark CSS → points
    };
}

/**
 * Vrai si le document porte des styles qui ne se laissent PAS réduire à une
 * propriété de calque — plusieurs couleurs, plusieurs polices, du gras partiel.
 * C'est ce qui décide s'il faut avertir l'utilisateur qu'un modèle ne pourra
 * pas tout rejouer sur un autre texte.
 */
export function aDesStylesPartiels(content) {
    if (!content || !content.content) return false;
    const a = analyzeContent(content);
    if (a.color.state === 'mixed' || a.fontFamily.state === 'mixed' || a.fontSize.state === 'mixed') return true;
    return a.bold === 'mixed' || a.italic === 'mixed' || a.underline === 'mixed';
}

/** Les attributs de `textStyle` que l'inspecteur pilote, et la propriété à plat
 *  qui leur correspond. */
const ATTRIBUT_POUR_CHAMP = { color: 'color', font: 'fontFamily', size: 'fontSize' };

/**
 * Retire des marks d'un document Tiptap ce qui masquerait une propriété à plat
 * qu'on vient de régler — et rien d'autre.
 *
 * Sans cela, choisir une couleur dans l'inspecteur sur un calque déjà édité au
 * double-clic n'a AUCUN effet visible : la mark reste et gagne au rendu.
 * L'infobulle de l'inspecteur promet pourtant d'« uniformiser toute la
 * sélection ». On tient la promesse ici.
 *
 * @param {object} content - document Tiptap (non muté)
 * @param {string[]} champs - parmi 'color', 'font', 'size', 'bold', 'italic'
 * @returns {object|null} le document nettoyé, ou `content` inchangé
 */
export function retirerMarksDeStyle(content, champs) {
    if (!content || typeof content !== 'object' || !champs || champs.length === 0) return content;
    const attributs = champs.map((c) => ATTRIBUT_POUR_CHAMP[c]).filter(Boolean);
    const marksEntieres = champs.filter((c) => c === 'bold' || c === 'italic' || c === 'underline');
    if (attributs.length === 0 && marksEntieres.length === 0) return content;

    const copie = JSON.parse(JSON.stringify(content));
    const parcourir = (noeud) => {
        if (!noeud || typeof noeud !== 'object') return;
        if (Array.isArray(noeud.marks)) {
            noeud.marks = noeud.marks.filter((m) => {
                if (!m) return false;
                if (marksEntieres.includes(m.type)) return false;
                if (m.type !== 'textStyle') return true;
                if (m.attrs) for (const a of attributs) delete m.attrs[a];
                // Une mark textStyle vidée de tous ses attributs ne sert plus à
                // rien : la garder ferait croire à un style posé.
                return !!m.attrs && Object.values(m.attrs).some((v) => v !== null && v !== undefined);
            });
            if (noeud.marks.length === 0) delete noeud.marks;
        }
        if (Array.isArray(noeud.content)) noeud.content.forEach(parcourir);
    };
    parcourir(copie);
    return copie;
}

/**
 * Impose un alignement à TOUS les paragraphes d'un document Tiptap.
 *
 * `stripStyleMarks` ne retire que les marks : les attributs de PARAGRAPHE, eux,
 * survivent. Or `richTextToHTML` émet `text-align` en style inline, qui l'emporte
 * sur le conteneur. Un calque édité une fois au double-clic gardait donc son
 * alignement d'alors, et un modèle .lfgr qui le voulait centré affichait un texte
 * à gauche — pendant que l'inspecteur, lui, affichait « centré » et que le PDF,
 * qui lit la propriété à plat, sortait bien centré. Trois vérités différentes.
 */
export function poserAlignementDansContenu(content, alignement) {
    if (!content || typeof content !== 'object' || !alignement) return content;
    const copie = JSON.parse(JSON.stringify(content));
    for (const noeud of (copie.content || [])) {
        if (noeud && noeud.type === 'paragraph') {
            noeud.attrs = { ...(noeud.attrs || {}), textAlign: alignement };
        }
    }
    return copie;
}
